// Generic pre-signed document upload service.
// Any upload surface that doesn't need Lead/Job's document-folder concept (Activity/FollowUp
// attachments, Email attachments, ...) should go through this instead of writing its own
// Initiate/Confirm flow. Mirrors uploadLeadDocuments in leadsService.js, minus folderId.
import axios from "axios";
import axiosInstance from "../utils/axios";

const DOCUMENT_UPLOAD_ENDPOINT = "api/DocumentUpload";

export const DocumentUploadFeature = {
  ACTIVITY_ATTACHMENT: "ActivityAttachment",
  EMAIL_ATTACHMENT: "EmailAttachment",
  TEMPLATE_ATTACHMENT: "TemplateAttachment",
  INCIDENT_REPORT_ATTACHMENT: "IncidentReportAttachment",
  TOOLBOX_TALK_ATTACHMENT: "ToolboxTalkAttachment",
  VEHICLE_ATTACHMENT: "VehicleAttachment",
  VEHICLE_CHECKLIST_ATTACHMENT: "VehicleChecklistAttachment",
  CONTRACTOR_DOCUMENT: "ContractorDocument",
  JOB_QR_CODE: "JobQRCode",
  VEHICLE_QR_CODE: "VehicleQRCode",
  USER_CERTIFICATIONS: "UserCertifications",
  CLIENT_NOTE_ATTACHMENT: "ClientNoteAttachment",
  COMPANY_CERTIFICATIONS: "CompanyCertifications",
  SELECTION_OPTION_ATTACHMENT: "SelectionOptionAttachment",
  JOB_SELECTION_SIGNED_DOCUMENT: "JobSelectionSignedDocument",
  USER_CARD_DOCUMENT: "UserCardDocument",
};

/**
 * Uploads files directly to storage via presigned URLs (Initiate -> PUT to storage -> Confirm).
 * @param {string} feature - One of DocumentUploadFeature.
 * @param {File[]} files
 * @returns {Promise<{success: boolean, data: Array<{fileName: string, filePath: string, fileExtension: string}>, message?: string}>}
 */
export const uploadAttachments = async (feature, files, extraParams = null) => {
  if (!files || files.length === 0) {
    return { success: true, data: [] };
  }

  const companyId = typeof extraParams === "object" && extraParams !== null
    ? (extraParams.companyId || extraParams.CompanyId)
    : extraParams;

  try {
    const fileMetas = files.map((file) => ({
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      fileSizeBytes: file.size,
    }));

    const initiatePayload = { feature, files: fileMetas };
    if (companyId) {
      initiatePayload.companyId = companyId;
    }

    const initiateResponse = await axiosInstance.post(
      `${DOCUMENT_UPLOAD_ENDPOINT}/Initiate`,
      initiatePayload,
    );
    const presignedItems = initiateResponse.data?.data || [];

    if (presignedItems.length !== files.length) {
      throw new Error("Upload initialization failed: mismatched file count.");
    }

    await Promise.all(
      files.map((file, index) =>
        axios.put(presignedItems[index].uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        }),
      ),
    );

    const confirmFiles = files.map((file, index) => {
      const dotIndex = file.name.lastIndexOf(".");
      return {
        fileKey: presignedItems[index].fileKey,
        fileName: file.name,
        fileExtension: dotIndex >= 0 ? file.name.slice(dotIndex) : "",
        contentType: file.type || "application/octet-stream",
      };
    });

    const confirmPayload = { feature, files: confirmFiles };
    if (companyId) {
      confirmPayload.companyId = companyId;
    }

    const confirmResponse = await axiosInstance.post(
      `${DOCUMENT_UPLOAD_ENDPOINT}/Confirm`,
      confirmPayload,
    );
    const results = confirmResponse.data?.data || [];
    const allSucceeded = results.length > 0 && results.every((r) => r.success);

    if (!allSucceeded) {
      const failedMessages = results
        .filter((r) => !r.success)
        .map((r) => `${r.fileName}: ${r.message || "Upload failed"}`);
      throw new Error(failedMessages.join(", ") || "Upload failed");
    }

    return {
      success: true,
      data: results.map((r) => ({
        fileName: r.fileName,
        filePath: r.fileKey,
        fileExtension: r.fileExtension,
        fileSizeKB: r.fileSizeKB || r.fileSizeKb || 0,
      })),
    };
  } catch (error) {
    console.error("Error uploading attachments:", error);
    return {
      success: false,
      message: error.message || "Failed to upload attachments",
      data: [],
    };
  }
};

/**
 * Downloads an already-uploaded attachment's raw bytes as a base64-encoded data URI, resolved
 * server-side rather than via a presigned URL. A plain <img src="presigned-url"> displays fine
 * cross-origin without any S3 CORS configuration, but embedding that same image into a
 * canvas-based PDF export (html2canvas) requires the canvas capture to actually read the pixel
 * bytes, which DOES need S3 CORS to cooperate — this sidesteps that by fetching the bytes through
 * our own same-origin API instead, at the cost of a slightly heavier response.
 * @param {string} fileKey - The storage key returned as `filePath` from uploadAttachments.
 * @returns {Promise<{success: boolean, dataUri?: string, message?: string}>}
 */
export const getAttachmentDataUri = async (fileKey, companyId = null, feature = null) => {
  if (!fileKey) {
    return { success: false, message: "No file key provided" };
  }

  try {
    let targetFeature = feature;
    if (!targetFeature && typeof fileKey === "string") {
      const lowerKey = fileKey.toLowerCase();
      if (lowerKey.startsWith("selection-option-docs/")) {
        targetFeature = DocumentUploadFeature.SELECTION_OPTION_ATTACHMENT;
      }
    }
    const params = { fileKey };
    if (companyId) params.companyId = companyId;
    if (targetFeature) params.feature = targetFeature;

    const response = await axiosInstance.get(`${DOCUMENT_UPLOAD_ENDPOINT}/Download`, { params });
    const { base64, contentType } = response.data?.data || {};
    if (!base64) {
      return { success: false, message: "File not found" };
    }
    return { success: true, dataUri: `data:${contentType || "application/octet-stream"};base64,${base64}` };
  } catch (error) {
    console.error("Error downloading attachment:", error);
    return {
      success: false,
      message: error.message || "Failed to download attachment",
    };
  }
};

/**
 * Gets a short-lived presigned URL for viewing/downloading an already-uploaded attachment
 * (e.g. an Activity or Email attachment referenced by its stored fileKey/filePath).
 * @param {string} fileKey - The storage key returned as `filePath` from uploadAttachments.
 * @returns {Promise<{success: boolean, url?: string, message?: string}>}
 */
export const getAttachmentPreviewUrl = async (fileKey, companyId = null, feature = null) => {
  if (!fileKey) {
    return { success: false, message: "No file key provided" };
  }

  try {
    const params = { fileKey };

    let targetCompanyId = companyId;
    let targetFeature = feature;

    // Handle overload where 2nd argument is feature string instead of companyId
    if (typeof companyId === "string" && !feature) {
      if (
        Object.values(DocumentUploadFeature).includes(companyId) ||
        companyId.endsWith("Attachment") ||
        companyId.endsWith("Code") ||
        companyId.endsWith("Document")
      ) {
        targetFeature = companyId;
        targetCompanyId = null;
      }
    }

    // Auto-detect feature from fileKey prefix if feature was not provided
    if (!targetFeature && typeof fileKey === "string") {
      const lowerKey = fileKey.toLowerCase();
      if (lowerKey.startsWith("selection-option-docs/")) {
        targetFeature = DocumentUploadFeature.SELECTION_OPTION_ATTACHMENT;
      } else if (lowerKey.startsWith("activity-docs/")) {
        targetFeature = DocumentUploadFeature.ACTIVITY_ATTACHMENT;
      } else if (lowerKey.startsWith("email-docs/")) {
        targetFeature = DocumentUploadFeature.EMAIL_ATTACHMENT;
      } else if (lowerKey.startsWith("client-note-docs/")) {
        targetFeature = DocumentUploadFeature.CLIENT_NOTE_ATTACHMENT;
      } else if (lowerKey.startsWith("template-docs/")) {
        targetFeature = DocumentUploadFeature.TEMPLATE_ATTACHMENT;
      } else if (lowerKey.startsWith("incident-docs/")) {
        targetFeature = DocumentUploadFeature.INCIDENT_REPORT_ATTACHMENT;
      } else if (lowerKey.startsWith("toolbox-talk-docs/")) {
        targetFeature = DocumentUploadFeature.TOOLBOX_TALK_ATTACHMENT;
      } else if (lowerKey.startsWith("vehicle-docs/")) {
        targetFeature = DocumentUploadFeature.VEHICLE_ATTACHMENT;
      } else if (lowerKey.startsWith("vehicle-checklist-docs/")) {
        targetFeature = DocumentUploadFeature.VEHICLE_CHECKLIST_ATTACHMENT;
      } else if (lowerKey.startsWith("contractor-docs/")) {
        targetFeature = DocumentUploadFeature.CONTRACTOR_DOCUMENT;
      } else if (lowerKey.startsWith("user-certifications/")) {
        targetFeature = DocumentUploadFeature.USER_CERTIFICATIONS;
      } else if (lowerKey.startsWith("company-certifications/")) {
        targetFeature = DocumentUploadFeature.COMPANY_CERTIFICATIONS;
      } else if (lowerKey.startsWith("user-card-docs/")) {
        targetFeature = DocumentUploadFeature.USER_CARD_DOCUMENT;
      }
    }

    if (targetCompanyId) {
      params.companyId = targetCompanyId;
    }
    if (targetFeature) {
      params.feature = targetFeature;
    }

    const response = await axiosInstance.get(`${DOCUMENT_UPLOAD_ENDPOINT}/PreviewUrl`, {
      params,
    });
    return { success: true, url: response.data?.data?.url };
  } catch (error) {
    console.error("Error getting attachment preview URL:", error);
    return {
      success: false,
      message: error.message || "Failed to get preview URL",
    };
  }
};
